import React from "react";
import { useParams } from "react-router-dom";

import firebase from "firebaseFactory";
import { Page } from "modules/layout/Page";
import { useAuthDataContext, Permissions } from "modules/auth";
import { useEventData } from "modules/events";
import { Heading2 } from "components/Heading2";

import { EventList } from "../events/components/EventList";

import { AggregatedDeviceCard } from "./components/AggregatedDeviceCard";
import { AggregatedDeviceStatus } from "./components/AggregatedDeviceStatus";
import {
  useSiteDataByUnit,
  useDevicesByUnit,
  flattenSiteDataByUnit,
} from "./useSiteData";
import { AggregatedDeviceStatsRow } from "./components/AggregatedDeviceStats";
import { QuickLink } from "./components/QuickLink";

export const SiteDashboard = ({
  getDevicesBySiteId = firebase.function("getDevicesBySiteId"),
  getNow = Date.now,
}) => {
  const { siteId } = useParams();
  const { enforcer } = useAuthDataContext();
  const { devicesByUnit, isLoading } = useDevicesByUnit(
    siteId,
    getDevicesBySiteId
  );
  const { eventData, isLoading: isEventDataLoading } = useEventData(
    siteId,
    undefined,
    undefined,
    3
  );
  const { latestDataByUnit } = useSiteDataByUnit(siteId);
  const devices = Object.values(devicesByUnit).flat();
  const deviceData = flattenSiteDataByUnit(latestDataByUnit);
  return (
    <Page
      isLoading={isLoading}
      titleSupplement={
        <AggregatedDeviceStatus
          devices={devices}
          deviceData={deviceData}
          getNow={getNow}
        />
      }
    >
      <div className="mb-6">
        <Heading2>Site Summary</Heading2>
        <AggregatedDeviceStatsRow deviceData={deviceData} getNow={getNow} />
      </div>
      <div className="mb-6">
        {enforcer.can(Permissions.Site_ViewEvents, { siteId }) && (
          <>
            <Heading2>Events</Heading2>
            <EventList
              eventData={eventData}
              isLoading={isEventDataLoading}
              linkMoreTo={`/sites/${siteId}/events`}
            />
          </>
        )}
      </div>
      <Heading2>Units in {siteId}</Heading2>
      <div className="flex flex-wrap -mx-2">
        {Object.entries(devicesByUnit).map(([unitId, devices], index) => (
          <AggregatedDeviceCard
            key={index}
            linkTo={`/sites/${siteId}/units/${unitId}/`}
            title={unitId}
            devices={devices}
            deviceData={Object.values(latestDataByUnit[unitId] || {})}
            getNow={getNow}
            isUnit={true}
            quickLinks={
              <>
                <QuickLink to={`/sites/${siteId}/units/${unitId}/command`}>
                  Send Command
                </QuickLink>
              </>
            }
          />
        ))}
      </div>
    </Page>
  );
};
